import { RouteLocationNormalizedGeneric } from "vue-router";
interface PagePath {
  [key: string]: string;
}

interface RouteMap {
  [key: string]: string;
}

interface UnDecodeRouteMap {
  [key: string]: string[];
}

interface ProtocolMap {
  [key: string]: string;
}

interface RouteParams {
  [key: string]: string;
}

interface AfterLoginRoutePushData {
  routeList: RouteLocationNormalizedGeneric[];
  isNavigating: boolean;
}

const pagePath: PagePath = {
  themeList: "/themeList",
};

/**
 * 路由映射配置
 * - 跳转型路由（默认）：返回对应的小程序url：/{{path}}?{{query}}
 * - 功能型路由：function_{{funName}}, funName为当前页面执行的方法名
 * - 特殊路由：
 *  mall_customerservice
 */
const ROUTE_MAP: RouteMap = {
  receive_voucher: `function_receiveVoucher`, //领取优惠券
  fetch_category: `function_fetchCategory`, //获取分类子页面
  mall_openH5: `{{path}}`, //H5页面
  mall_thematicpage: `${pagePath.themeList}/{{pageid}}`, //专题页
  mall_customerservice: `customerService`, //客服页
  story_vippage: "vipPage",
  mall_openrednote: `{{url}}`,
};

// 不需要解码的参数
const UN_DECODE_ROUTE_MAP: UnDecodeRouteMap = {
  story_mall: ["url"],
};
class RouteParser {
  /**
   * 编码参数值
   * @param value - 要编码的值
   * @returns 编码后的字符串
   */
  static encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * 解码参数值
   * @param value - 要解码的值
   * @returns 解码后的字符串
   */
  static decodeValue(value: string): string {
    return decodeURIComponent(value);
  }

  /**
   * 解析路由字符串
   * @param route - 路由字符串，格式如：action={{code}}&key1=val1&key2=val2
   * @returns 解析后的路径或null（解析失败时）
   */
  static parse(route: string): string | null {
    let params = this.getAllParams(route);
    if (!params.action) {
      console.error("缺少action参数：", route);
      return null;
    }

    const { action } = params;
    const template = ROUTE_MAP[action];
    if (!template) {
      console.error("当前路由不支持：", route);
      return null;
    }

    let finalPath = template;
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        let value = params[key];
        const placeholder = new RegExp(`{{${key}}}`, "g");
        finalPath = finalPath.replace(placeholder, value);
      }
    }

    return finalPath;
  }

  /**
   * 获取路由字符串中的指定参数
   * @param route - 路由字符串
   * @param key - 参数名
   * @returns 参数值或undefined
   */
  static getParam(route: string, key: string): string | undefined {
    let params = this.getAllParams(route);
    return params[key];
  }

  /**
   * 获取路由字符串中的所有参数
   * @param route - 路由字符串
   * @returns 参数对象
   */
  static getAllParams(route: string): RouteParams {
    if (!route) {
      console.error("无效的路由字符串");
      return {};
    }

    const params: RouteParams = {};
    const pairs = route.split("&");

    pairs.forEach((pair) => {
      let [key, value] = pair.split("=");
      if (key && value !== undefined) {
        // 优化：将UN_DECODE_ROUTE_MAP[params.action]改为局部变量，避免重复查找
        const shouldNotDecode = UN_DECODE_ROUTE_MAP[params.action]?.includes(key);
        params[key] = shouldNotDecode ? value : this.decodeValue(value);
      }
    });
    return params;
  }
}

/**
 * 协议链接映射配置
 * - 默认返回对应的小程序url：/{{path}}?{{query}}
 * - 功能型路由：function_{{funName}}, funName为当前页面执行的方法名
 */
const PROTOCOL_MAP = {
  "babybus://util/openWebView": `/pages/webview/webview?url={{url}}`,
};

class ProtocolParser {
  /**
   * 编码参数值
   * @param value - 要编码的值
   * @returns 编码后的字符串
   */
  static encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * 解析协议链接
   * @param protocol - 协议字符串，格式如：babybus://{{code}}={&key1=val1&key2=val2}
   * @returns 解析后的路径或null（解析失败时）
   */
  static parse(protocol: string): string | null {
    const protocolName = this.getProtocolName(protocol);
    const template = PROTOCOL_MAP[protocolName];

    if (!template) {
      console.error("当前模板链接不支持：", protocol);
      return null;
    }

    const params = this.getAllParams(protocol);
    let finalPath = template;
    for (const key in params) {
      let value = params[key];
      if (typeof value === "string") {
        value = value.indexOf("?") > -1 ? this.encodeValue(value) : value;
      }
      const placeholder = new RegExp(`{{${key}}}`, "g");
      finalPath = finalPath.replace(placeholder, value);
    }

    return finalPath;
  }

  /**
   * 获取协议名称
   * @param protocol - 协议字符串
   * @returns 协议名称
   */
  static getProtocolName(protocol: string): string {
    const regex = /^babybus:\/\/([^\/]+\/[^\/\?]+)/;
    const match = protocol.match(regex);
    return match ? match[0] : protocol;
  }

  /**
   * 获取协议链接中的指定参数
   * @param protocol - 协议链接
   * @param key - 参数名
   * @returns 参数值或undefined
   */
  static getParam(protocol: string, key: string): string | undefined {
    let params = this.getAllParams(protocol);
    return params[key];
  }

  /**
   * 获取协议链接中的所有参数
   * @param protocol - 协议链接
   * @returns 参数对象
   */
  static getAllParams(protocol: string): RouteParams {
    if (!protocol) {
      console.error("无效的链接字符串");
      return {};
    }

    const params: RouteParams = {};
    let parts = protocol.split("?");

    if (parts.length < 2) {
      return params;
    }

    // 解析 URL 的 query 部分
    try {
      // 解码 query 部分并解析 JSON
      const queryString = decodeURIComponent(parts[1]);
      Object.assign(params, JSON.parse(queryString));
    } catch (error) {
      console.error("解析参数部分时出错:", error);
    }

    return params;
  }
}

import Util from "@/utils/util";
import { useGlobalStore, useUserStore } from "@/stores/index";
import Router from "@/router";
class RouteHelper {
  /**
   * 导航到首页
   * 处理各种情况下的首页导航逻辑
   */
  static navHome(): void {
    const store = useGlobalStore();
    const historyStack = store.$state.historyStack;

    if (historyStack.length > 1) {
      const homeIndex = historyStack.findIndex((route) => route.name === "home" || route.name === "index");
      if (homeIndex !== -1) {
        // 如果已访问过首页，计算返回的步数
        const backValue = (historyStack.length - homeIndex - 1) * -1;
        Router.go(backValue);
      } else {
        store.$state.isNavigatingHome = true;
        // 如果没有访问过首页，直接返回到第一个页面
        history.go(-1 * (historyStack.length - 1));
      }
    } else {
      store.$state.isNavigatingHome = true;
      store.$state.keepAliveComponents = [];
      store.$state.historyStack = [];
      Router.replace({ name: "index" });

      if (!Util.isInMiniProgram()) {
        history.replaceState(null, null, "");
      }
    }
    const previousRoute = Router.currentRoute.value; // 存储当前路由
    // 设置定时器检查路由是否发生变化
    setTimeout(() => {
      // 检查路由是否发生变化（如果返回导航成功，则路由变化）
      if (Router.currentRoute.value === previousRoute) {
        console.log("返回导航失败，直接跳转首页。");
        Router.replace({ name: "index" });
      }
    }, 500);
  }

  /**
   * 获取返回的步数
   * @param {Object} to - 目标路由对象
   * @param {Array} historyStack - 历史路由栈
   * @returns {number} 返回的步数
   */
  static getBackCount(to: RouteLocationNormalizedGeneric, historyStack: RouteLocationNormalizedGeneric[]): number {
    let backCount = 0;
    if (historyStack.length > 1) {
      for (let i = 0; i < historyStack.length; i++) {
        if (historyStack[i].fullPath === to.fullPath) {
          backCount = historyStack.length - 1 - i;
          break;
        }
      }
    }
    return backCount;
  }

  /**
   * 更新历史路由栈
   * @param to - 目标路由对象
   */

  static updateHistoryStack(to: RouteLocationNormalizedGeneric): void {
    const store = useGlobalStore();
    const historyStack = [...store.$state.historyStack];
    const backCount = this.getBackCount(to, historyStack);
    // console.log("backCount", backCount);
    // console.log("historyStack", historyStack);
    if (backCount > 0) {
      store.$state.historyStack = historyStack.slice(0, historyStack.length - backCount);
    } else {
      const lastHistory = historyStack.length ? historyStack[historyStack.length - 1] : {};
      const userStore = useUserStore();

      // 排除快速点击同一个页面和未登录时点击需要登录的页面
      if (!(to.meta?.requireAuth && !userStore.$state.isLogin)) {
        //如果属于本页面路由参数修改跳转，需要替换掉保存的原路由参数
        if (to.path === lastHistory.path && to.fullPath !== lastHistory.fullPath) {
          const lastIndex = store.$state.historyStack.length - 1;
          store.$state.historyStack[lastIndex] = to;
        } else {
          store.$state.historyStack.push(to);
        }
      }
    }
  }
  /**
   * 更新 keep-alive 缓存
   * 该方法用于根据目标路由对象 `to` 和历史路由栈 `historyStack` 更新 `keep-alive` 缓存。
   * 如果目标路由已经在历史栈中存在，则移除相应的 `keep-alive` 缓存。
   * 如果目标路由不在历史栈中，则根据需要添加 `keep-alive` 缓存。
   *
   * @param {Object} to - 目标路由对象，包含路由的路径、元信息等。
   */
  static updateKeepAliveCache(to: RouteLocationNormalizedGeneric, fromType: string): void {
    const store = useGlobalStore();
    const historyStack = [...store.$state.historyStack];
    const backCount = this.getBackCount(to, historyStack);
    if (backCount > 0) {
      if (fromType === "beforeEach") return;
      const backPageList = [].concat(historyStack).slice(historyStack.length - backCount, historyStack.length);
      backPageList.forEach((backPage) => {
        if (backPage.meta && backPage.meta.keepAlive) {
          this.removeKeepAliveCache(backPage.name);
        }
      });
    } else {
      if (fromType === "afterEach") return;
      const lastHistory = historyStack.length ? historyStack[historyStack.length - 1] : {};
      const userStore = useUserStore();
      if (to.path !== lastHistory.path && !(to.meta?.requireAuth && !userStore.$state.isLogin)) {
        if (to.meta?.keepAlive) {
          this.addKeepAliveCache(to.name);
        }
      }
    }
  }

  /**
   * 处理点击跳转首页逻辑
   * @param to - 目标路由对象
   * @param next - 路由next函数
   */
  static handleHomeNavigation(to: RouteLocationNormalizedGeneric, next: (route?: any) => void): void {
    const store = useGlobalStore();
    const { historyStack } = store.$state;
    store.$state.isNavigatingHome = false;

    //页面栈中无首页时会强制跳转至第一个页面，这是重定向至首页
    if (to.name !== "index") {
      const backPageList = [].concat(historyStack).splice(1);
      backPageList.forEach((backPage) => {
        if (backPage.meta && backPage.meta.keepAlive) {
          this.removeKeepAliveCache(backPage.name);
        }
      });
      store.$state.historyStack = historyStack.slice(0, 1);
      next({ name: "index" });
    } else {
      //如果页面栈中有首页则需要处理一下手动记录的历史状态
      const foundIndex = historyStack.findIndex((item) => item.name === "index");
      if (foundIndex !== -1) {
        const backPageList = [].concat(historyStack).splice(foundIndex + 1);
        backPageList.forEach((backPage) => {
          if (backPage.meta && backPage.meta.keepAlive) {
            this.removeKeepAliveCache(backPage.name);
          }
        });
        store.$state.historyStack = historyStack.slice(0, foundIndex + 1);
      }
      next();
    }
  }

  /**
   * 添加 keep-alive 缓存
   * @param componentName - 组件名称
   */
  static addKeepAliveCache(componentName: RouteLocationNormalizedGeneric["name"]): void {
    const store = useGlobalStore();
    const keepAliveComponents = store.$state.keepAliveComponents;
    const nameStr = typeof componentName === "symbol" ? componentName.toString() : componentName;

    // 这里原先有判断name是否相同再push的逻辑，现已移除以允许重复添加，主要原因是为了处理一个相同页面跳转相同页面，例如专题页跳转专题页，保证每个专题页之间都能有keepAlive缓存
    keepAliveComponents.push(nameStr);
  }

  /**
   * 移除 keep-alive 缓存
   * @param componentName - 组件名称
   */
  static removeKeepAliveCache(componentName: RouteLocationNormalizedGeneric["name"]): void {
    const store = useGlobalStore();
    const keepAliveComponents = store.$state.keepAliveComponents;
    const nameStr = typeof componentName === "symbol" ? componentName.toString() : componentName;
    const index = keepAliveComponents.lastIndexOf(nameStr);
    if (index > -1) {
      keepAliveComponents.splice(index, 1);
    }
  }

  /**
   * 登录后返回路由检查
   * @param afterLoginRoutePushData - 登录后路由推送数据
   */
  static loginBackRouteCheck(afterLoginRoutePushData: AfterLoginRoutePushData): void {
    const { routeList, isNavigating } = afterLoginRoutePushData;

    if (!routeList.length) {
      const loginLoadElement = document.getElementById("login-load");
      if (loginLoadElement?.style.display === "flex") {
        setTimeout(() => {
          if (loginLoadElement) {
            loginLoadElement.style.display = "none";
          }
        }, 500);
      }
      return;
    }

    const _routeList = Util.deepCopy(routeList);

    //如果登录后返回刷新的页面和缓存的第一页相同则直接shift跳转至第二页开始
    if (Router.currentRoute.value.name === routeList[0]?.name) {
      routeList.shift();
    }

    routeList.shift();
    afterLoginRoutePushData.routeList = routeList;
    afterLoginRoutePushData.isNavigating = !!routeList.length;

    //如果登录后返回刷新的页面和缓存的第一页相同则直接shift跳转至第二页开始
    if (Router.currentRoute.value.name === _routeList[0]?.name) {
      _routeList.shift();
      const targetPage = _routeList[0];
      if (targetPage) Router.push(targetPage);
    } else {
      const targetPage = _routeList[0];
      if (targetPage) {
        if (isNavigating) {
          Router.push(targetPage);
        } else {
          //首次需要执行replace覆盖掉之前返回刷新的页面
          Router.replace(targetPage);
        }
      }
    }
  }
}
export { RouteParser, ProtocolParser, RouteHelper };
